import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { addMonths, longDate, monthDays, monthLabel } from "@/lib/dates";
import { AdminLayout, Panel } from "@/components/admin/AdminLayout";
import { AppointmentCard } from "@/components/admin/AppointmentCard";

const WEEK = ["S", "M", "T", "W", "T", "F", "S"];
const navBtn = "grid h-11 w-11 place-items-center rounded-md border border-zinc-700 text-zinc-200 hover:bg-zinc-800";

const Calendar = ({ month, today, selected, byDay, onMonth, onSelect }) => {
  const { lead, days } = monthDays(month);
  return (
    <Panel testId="appointments-calendar">
      <div className="mb-3 flex items-center gap-2">
        <button onClick={() => onMonth(addMonths(month, -1))} className={navBtn} aria-label="Previous month" data-testid="calendar-prev-button"><ChevronLeft className="h-5 w-5" /></button>
        <h2 className="font-display flex-1 text-center text-2xl text-[#FAF7F2] sm:text-3xl" data-testid="calendar-month-label">{monthLabel(month)}</h2>
        <button onClick={() => onMonth(addMonths(month, 1))} className={navBtn} aria-label="Next month" data-testid="calendar-next-button"><ChevronRight className="h-5 w-5" /></button>
        <button onClick={() => { onMonth(today.slice(0, 7)); onSelect(today); }} className="h-11 rounded-md border border-[#FFD23F]/60 px-3 text-sm font-semibold text-[#FFD23F] hover:bg-[#FFD23F]/10" data-testid="calendar-today-button">Today</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-zinc-500">
        {WEEK.map((d, i) => <div key={i} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, i) => <div key={`b${i}`} />)}
        {days.map((d) => {
          const list = byDay[d] || [];
          const isSel = d === selected;
          return (
            <button key={d} onClick={() => onSelect(d)}
              className={`flex min-h-[52px] min-w-0 flex-col items-stretch rounded-md border p-1 text-left transition-colors sm:min-h-[84px] ${isSel ? "border-[#FFD23F] bg-[#FFD23F]/10" : "border-zinc-800 bg-[#121214] hover:border-zinc-600"}`}
              data-testid={`calendar-day-${d}`} aria-label={`${longDate(d)}, ${list.length} appointments`}>
              <span className="flex items-center justify-between">
                <span className={`grid h-6 w-6 place-items-center rounded-full text-sm ${d === today ? "bg-[#FFD23F] font-bold text-[#121214]" : "text-zinc-200"}`}>{Number(d.slice(8))}</span>
                {list.length > 0 && <span className="rounded-full bg-[#C8322B] px-1.5 text-[11px] font-bold text-white" data-testid={`calendar-day-count-${d}`}>{list.length}</span>}
              </span>
              <span className="mt-1 hidden space-y-0.5 sm:block">
                {list.slice(0, 2).map((a) => (
                  <span key={a.id} className={`block truncate text-[11px] leading-tight ${a.status === "Cancelled" ? "text-zinc-500 line-through" : "text-zinc-300"}`}>{a.name}{a.vehicle && ` · ${a.vehicle}`}</span>
                ))}
                {list.length > 2 && <span className="block text-[11px] text-zinc-500">+{list.length - 2} more</span>}
              </span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
};

export default function AppointmentsPage() {
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [month, setMonth] = useState(null);
  const [selected, setSelected] = useState(params.get("date"));
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/admin/appointments/overview").then(({ data }) => {
      setOverview(data);
      setSelected((s) => s || data.today);
      setMonth((m) => m || (params.get("date") || data.today).slice(0, 7));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!month) return;
    api.get("/admin/appointments", { params: { start: `${month}-01`, end: `${month}-31` } }).then((r) => setItems(r.data));
  }, [month]);

  const byDay = useMemo(() => items.reduce((acc, a) => ({ ...acc, [a.dropoff_date]: [...(acc[a.dropoff_date] || []), a] }), {}), [items]);
  const select = (d) => {
    setSelected(d);
    setParams({ date: d }, { replace: true });
  };
  const dayList = byDay[selected] || [];

  return (
    <AdminLayout title="Appointments" testId="admin-appointments-page">
      <Link to="/admin/appointments/new" className="btn-gold mb-6 h-14 w-full text-lg" data-testid="new-appointment-button"><Plus className="h-5 w-5" /> New appointment</Link>
      {month && overview && (
        <Calendar month={month} today={overview.today} selected={selected} byDay={byDay} onMonth={setMonth} onSelect={select} />
      )}
      {selected && (
        <Panel title={longDate(selected)} testId="appointments-day-panel">
          {dayList.length === 0 && <p className="mb-3 text-zinc-400" data-testid="day-empty">No drop-offs on this day.</p>}
          <div className="mb-3 space-y-2" data-testid="day-appointments">{dayList.map((a) => <AppointmentCard key={a.id} a={a} showDate={false} />)}</div>
          <Link to={`/admin/appointments/new?date=${selected}`} className="flex h-12 items-center justify-center gap-2 rounded-md border border-zinc-700 text-base text-zinc-100 hover:bg-zinc-800" data-testid="day-add-appointment-button">
            <Plus className="h-4 w-4" /> Add appointment on this date
          </Link>
        </Panel>
      )}
      <Panel title="In the shop" testId="appointments-in-shop">
        {overview?.in_shop.length === 0 && <p className="text-zinc-400">Nothing in the shop right now.</p>}
        <div className="space-y-2">{overview?.in_shop.map((a) => <AppointmentCard key={a.id} a={a} />)}</div>
      </Panel>
      <Panel title="Upcoming" testId="appointments-upcoming">
        {overview?.upcoming.length === 0 && <p className="text-zinc-400">No upcoming drop-offs.</p>}
        <div className="space-y-2">{overview?.upcoming.map((a) => <AppointmentCard key={a.id} a={a} />)}</div>
      </Panel>
    </AdminLayout>
  );
}
